/*
 * What a person may do in teamBase, read in one place. The server derives a human's standing from
 * the special groups when it reads the account — a member of Administrators is an admin, a member of
 * Team members a team member, and a person in neither a guest — and sends it as the wire's `role`
 * ("" for a bot). Every surface that opens or hides a door by standing asks the predicates below
 * instead of comparing that word itself, so the day the words change, one file changes
 * (`standing.test.ts` scans the tree for a comparison anywhere else).
 *
 * The client's answer only decides which doors are drawn. The server decides what is allowed, at
 * every route, from the same rows; a door drawn by mistake ends in a refusal, never in a write.
 */

import { t, type PlainKey } from "../i18n/index.ts"
import type { PublicUser } from "../store/model.ts"

/** The three standings, in the order the New account form offers them. */
export const STANDINGS = ["member", "guest", "admin"] as const
export type Standing = (typeof STANDINGS)[number]

/** What standing is read from: an account as the wire sends it (`PublicUser`, `SelfUser`, `PersonView`). */
export type StandingSource = Pick<PublicUser, "kind" | "role">

/** Each standing's word, one key per standing, so a new standing does not compile without one. */
const STANDING_LABELS: Record<Standing, PlainKey> = {
	member: "people.standing.member",
	guest: "people.standing.guest",
	admin: "people.standing.admin",
}

/** Whether a word from the wire names a standing this build knows. */
function isStanding(word: string): word is Standing {
	return (STANDINGS as readonly string[]).includes(word)
}

/**
 * A human's standing from the wire's derived `role`. A bot has none (it is outside the groups), and a
 * word this build does not know — a newer server's — is no standing either, so it opens no door.
 */
export function standingOf(user: StandingSource | null | undefined): Standing | undefined {
	if (user === null || user === undefined || user.kind === "bot") return undefined
	return isStanding(user.role) ? user.role : undefined
}

/** A member of Administrators. */
export function isAdmin(user: StandingSource | null | undefined): boolean {
	return standingOf(user) === "admin"
}

/** A member of Team members: an admin is one too, a guest never. */
export function isTeamMember(user: StandingSource | null | undefined): boolean {
	const standing = standingOf(user)
	return standing === "admin" || standing === "member"
}

/** A person in neither special group. */
export function isGuest(user: StandingSource | null | undefined): boolean {
	return standingOf(user) === "guest"
}

/** The word a standing is shown with. */
export function standingLabel(standing: Standing): string {
	return t(STANDING_LABELS[standing])
}

/**
 * What a row says the account is: *Bot* for a bot, else the standing's word, else nothing — an
 * unknown word is left unsaid rather than shown raw.
 */
export function accessLabel(user: StandingSource): string {
	if (user.kind === "bot") return t("people.kind.bot")
	const standing = standingOf(user)
	return standing === undefined ? "" : standingLabel(standing)
}
